import * as XLSX from 'xlsx';

import type { ReservaSofLinha } from '../types';

export type ReservaSofImportada = Omit<
  ReservaSofLinha,
  'id' | 'competencia' | 'ultimaSincronizacao'
>;

export interface ResultadoImportacaoReservas {
  linhas: ReservaSofImportada[];
  erros: string[];
}

const COLUNAS_OBRIGATORIAS = ['Reserva', 'Dotação', 'Saldo da reserva'];

function textoOuVazio(valor: unknown): string {
  if (valor == null) return '';
  return String(valor).trim();
}

function numeroOuZero(valor: unknown): number {
  if (typeof valor === 'number') return valor;
  const texto = String(valor ?? '')
    .replace(/\./g, '')
    .replace(',', '.');
  const numero = parseFloat(texto);
  return Number.isFinite(numero) ? numero : 0;
}

/** Lê planilha .xlsx/.csv de reservas SOF (mesmo formato do "Exportar CSV" desta tela). */
export async function parseReservasSofArquivo(arquivo: File): Promise<ResultadoImportacaoReservas> {
  const bytes = await arquivo.arrayBuffer();
  const livro = XLSX.read(bytes, { type: 'array' });
  const primeiraAba = livro.SheetNames[0];
  if (!primeiraAba) {
    return { linhas: [], erros: ['Planilha sem abas.'] };
  }
  const planilha = livro.Sheets[primeiraAba];
  if (!planilha) {
    return { linhas: [], erros: ['Planilha sem abas.'] };
  }
  const registros = XLSX.utils.sheet_to_json<Record<string, unknown>>(planilha, { defval: '' });

  const primeiroRegistro = registros[0];
  if (!primeiroRegistro) {
    return { linhas: [], erros: ['Planilha vazia.'] };
  }

  const cabecalho = Object.keys(primeiroRegistro);
  const faltando = COLUNAS_OBRIGATORIAS.filter((coluna) => !cabecalho.includes(coluna));
  if (faltando.length > 0) {
    return { linhas: [], erros: [`Coluna(s) obrigatória(s) ausente(s): ${faltando.join(', ')}.`] };
  }

  const linhas: ReservaSofImportada[] = [];
  const erros: string[] = [];

  registros.forEach((registro, indice) => {
    const codigoReserva = textoOuVazio(registro['Reserva']);
    const dotacao = textoOuVazio(registro['Dotação']);
    if (!codigoReserva || !dotacao) {
      erros.push(`Linha ${indice + 2}: "Reserva" e "Dotação" são obrigatórios.`);
      return;
    }
    linhas.push({
      codigoReserva,
      dotacao,
      saldoReserva: numeroOuZero(registro['Saldo da reserva']).toFixed(2),
      subfuncaoCodigo: textoOuVazio(registro['Sub-função código']),
      subfuncaoTexto: textoOuVazio(registro['Sub-função texto']),
      programa: textoOuVazio(registro['Programa']),
      projetoAtividade: textoOuVazio(registro['Projeto/Atividade']),
      contaDespesa: textoOuVazio(registro['Conta despesa']),
    });
  });

  return { linhas, erros };
}
